import type { ScenarioInput } from '@/types/supremeIntelligenceTypes';

export interface ScenarioTemplate {
  id: string;
  name: string;
  description: string;
  category: 'buyer' | 'investor' | 'refinance' | 'special';
  icon: string;
  scenarios: Omit<ScenarioInput, 'id'>[];
}

export const SCENARIO_TEMPLATES: ScenarioTemplate[] = [
  {
    id: 'first-time-buyer',
    name: 'First-Time Buyer',
    description: 'Lower down payment options for first-time homebuyers with FHA and conventional loans',
    category: 'buyer',
    icon: 'Home',
    scenarios: [
      {
        name: 'FHA 3.5% Down',
        homePrice: 350000,
        downPaymentPercentage: 3.5,
        interestRate: 6.75,
        loanTerm: 30,
        propertyTaxRate: 1.2,
        homeInsurance: 1200,
        hoaFees: 0,
      },
      {
        name: 'Conventional 5% Down',
        homePrice: 350000,
        downPaymentPercentage: 5,
        interestRate: 6.625,
        loanTerm: 30,
        propertyTaxRate: 1.2,
        homeInsurance: 1200,
        hoaFees: 0,
      },
      {
        name: 'Conventional 10% Down',
        homePrice: 350000,
        downPaymentPercentage: 10,
        interestRate: 6.5,
        loanTerm: 30,
        propertyTaxRate: 1.2,
        homeInsurance: 1200,
        hoaFees: 0,
      },
    ],
  },
  {
    id: 'investment-property',
    name: 'Investment Property',
    description: 'Scenarios for rental properties with different down payments and cash flow projections',
    category: 'investor',
    icon: 'TrendingUp',
    scenarios: [
      {
        name: '20% Down - Conservative',
        homePrice: 400000,
        downPaymentPercentage: 20,
        interestRate: 7.25,
        loanTerm: 30,
        propertyTaxRate: 1.5,
        homeInsurance: 1500,
        hoaFees: 250,
      },
      {
        name: '25% Down - Moderate',
        homePrice: 400000,
        downPaymentPercentage: 25,
        interestRate: 7.0,
        loanTerm: 30,
        propertyTaxRate: 1.5,
        homeInsurance: 1500,
        hoaFees: 250,
      },
      {
        name: '30% Down - Aggressive',
        homePrice: 400000,
        downPaymentPercentage: 30,
        interestRate: 6.875,
        loanTerm: 30,
        propertyTaxRate: 1.5,
        homeInsurance: 1500,
        hoaFees: 250,
      },
    ],
  },
  {
    id: 'refinance-options',
    name: 'Refinance Options',
    description: 'Compare different refinancing scenarios to lower your rate or change loan terms',
    category: 'refinance',
    icon: 'RefreshCw',
    scenarios: [
      {
        name: 'Current Loan',
        homePrice: 450000,
        downPaymentPercentage: 20,
        interestRate: 7.5,
        loanTerm: 30,
        propertyTaxRate: 1.3,
        homeInsurance: 1400,
        hoaFees: 150,
      },
      {
        name: 'Refinance - Lower Rate',
        homePrice: 450000,
        downPaymentPercentage: 20,
        interestRate: 6.25,
        loanTerm: 30,
        propertyTaxRate: 1.3,
        homeInsurance: 1400,
        hoaFees: 150,
      },
      {
        name: 'Refinance - 15 Year',
        homePrice: 450000,
        downPaymentPercentage: 20,
        interestRate: 5.75,
        loanTerm: 15,
        propertyTaxRate: 1.3,
        homeInsurance: 1400,
        hoaFees: 150,
      },
    ],
  },
  {
    id: 'move-up-buyer',
    name: 'Move-Up Buyer',
    description: 'Scenarios for buyers upgrading to a larger home using equity from current property',
    category: 'buyer',
    icon: 'MoveUp',
    scenarios: [
      {
        name: 'Using 15% Down',
        homePrice: 600000,
        downPaymentPercentage: 15,
        interestRate: 6.625,
        loanTerm: 30,
        propertyTaxRate: 1.4,
        homeInsurance: 1800,
        hoaFees: 200,
      },
      {
        name: 'Using 20% Down',
        homePrice: 600000,
        downPaymentPercentage: 20,
        interestRate: 6.5,
        loanTerm: 30,
        propertyTaxRate: 1.4,
        homeInsurance: 1800,
        hoaFees: 200,
      },
      {
        name: 'Using 25% Down',
        homePrice: 600000,
        downPaymentPercentage: 25,
        interestRate: 6.375,
        loanTerm: 30,
        propertyTaxRate: 1.4,
        homeInsurance: 1800,
        hoaFees: 200,
      },
    ],
  },
  {
    id: 'va-loan',
    name: 'VA Loan (Military)',
    description: 'Zero down payment options for eligible veterans and active military',
    category: 'special',
    icon: 'Shield',
    scenarios: [
      {
        name: 'VA 0% Down',
        homePrice: 425000,
        downPaymentPercentage: 0,
        interestRate: 6.25,
        loanTerm: 30,
        propertyTaxRate: 1.2,
        homeInsurance: 1300,
        hoaFees: 0,
      },
      {
        name: 'VA 5% Down - Better Rate',
        homePrice: 425000,
        downPaymentPercentage: 5,
        interestRate: 6.0,
        loanTerm: 30,
        propertyTaxRate: 1.2,
        homeInsurance: 1300,
        hoaFees: 0,
      },
      {
        name: 'VA 10% Down - Best Rate',
        homePrice: 425000,
        downPaymentPercentage: 10,
        interestRate: 5.875,
        loanTerm: 30,
        propertyTaxRate: 1.2,
        homeInsurance: 1300,
        hoaFees: 0,
      },
    ],
  },
  {
    id: 'jumbo-loan',
    name: 'Jumbo Loan',
    description: 'High-value property scenarios for loans above conforming limits',
    category: 'special',
    icon: 'Building2',
    scenarios: [
      {
        name: '20% Down - Jumbo',
        homePrice: 850000,
        downPaymentPercentage: 20,
        interestRate: 7.125,
        loanTerm: 30,
        propertyTaxRate: 1.6,
        homeInsurance: 2500,
        hoaFees: 400,
      },
      {
        name: '25% Down - Jumbo',
        homePrice: 850000,
        downPaymentPercentage: 25,
        interestRate: 6.875,
        loanTerm: 30,
        propertyTaxRate: 1.6,
        homeInsurance: 2500,
        hoaFees: 400,
      },
      {
        name: '30% Down - Jumbo',
        homePrice: 850000,
        downPaymentPercentage: 30,
        interestRate: 6.75,
        loanTerm: 30,
        propertyTaxRate: 1.6,
        homeInsurance: 2500,
        hoaFees: 400,
      },
    ],
  },
  {
    id: 'downsizing',
    name: 'Downsizing',
    description: 'Scenarios for retirees or empty-nesters moving to a smaller, more affordable home',
    category: 'buyer',
    icon: 'Minimize2',
    scenarios: [
      {
        name: 'Cash Purchase',
        homePrice: 275000,
        downPaymentPercentage: 100,
        interestRate: 0,
        loanTerm: 30,
        propertyTaxRate: 1.1,
        homeInsurance: 900,
        hoaFees: 180,
      },
      {
        name: '50% Down - 15 Year',
        homePrice: 275000,
        downPaymentPercentage: 50,
        interestRate: 5.875,
        loanTerm: 15,
        propertyTaxRate: 1.1,
        homeInsurance: 900,
        hoaFees: 180,
      },
      {
        name: '40% Down - 30 Year',
        homePrice: 275000,
        downPaymentPercentage: 40,
        interestRate: 6.25,
        loanTerm: 30,
        propertyTaxRate: 1.1,
        homeInsurance: 900,
        hoaFees: 180,
      },
    ],
  },
];

/**
 * Get a template by ID
 */
export const getTemplateById = (id: string): ScenarioTemplate | undefined => {
  return SCENARIO_TEMPLATES.find(template => template.id === id);
};

/**
 * Get templates by category
 */
export const getTemplatesByCategory = (category: ScenarioTemplate['category']): ScenarioTemplate[] => {
  return SCENARIO_TEMPLATES.filter(template => template.category === category);
};

/**
 * Convert template scenarios to ScenarioInput format
 */
export const templateToScenarios = (template: ScenarioTemplate): ScenarioInput[] => {
  return template.scenarios.map(scenario => ({
    ...scenario,
    id: crypto.randomUUID(),
  }));
};
